import { useJsonViewerContext } from '~/modules/common/json-viewer/context';
import { jsonViewerTheme } from '~/modules/common/json-viewer/theme';
import { cn } from '~/utils/cn';

export interface SchemaConstraints {
  maxLength?: number;
  minLength?: number;
  maximum?: number;
  minimum?: number;
}

export interface SchemaLabelsProps {
  typeValue: string | string[] | null;
  /** The full `$ref`, such as '#/components/schemas/User'. */
  refValue: string | null;
  contentTypeValue?: string | null;
  hasAnyOf?: boolean;
  hasOneOf?: boolean;
  constraints?: SchemaConstraints | null;
}

export function SchemaLabels({ typeValue, refValue, contentTypeValue, hasAnyOf, hasOneOf, constraints }: SchemaLabelsProps) {
  const { renderRef } = useJsonViewerContext();
  if (!typeValue && !refValue && !contentTypeValue && !hasAnyOf && !hasOneOf && !constraints) return null;

  const typeValues = typeValue ? (Array.isArray(typeValue) ? typeValue : [typeValue]) : [];

  // anyOf takes precedence over oneOf when both are present.
  const compositionLabel = hasAnyOf ? 'anyOf' : hasOneOf ? 'oneOf' : null;

  return (
    <>
      {typeValues.length > 0 && (
        // One unit: a union wraps whole, with no pipe left at the end of a line.
        <span>
          {typeValues.map((type, index) => (
            <span key={type}>
              <span className={cn(jsonViewerTheme.schemaType, 'ml-0.5 px-1 py-0.5 text-xs')}>{type}</span>
              {index < typeValues.length - 1 && <span className="mx-1 text-muted-foreground">|</span>}
            </span>
          ))}
        </span>
      )}
      {compositionLabel && <span className={cn(jsonViewerTheme.badge, 'ml-0.5 bg-muted text-code-type')}>{compositionLabel}</span>}
      {refValue && (
        <span className={cn(jsonViewerTheme.badge, 'ml-0.5 bg-primary/10 text-primary')}>
          {renderRef ? renderRef(refValue) : refValue.split('/').pop()}
        </span>
      )}
      {contentTypeValue && <span className="ml-1 text-muted-foreground text-xs italic">{contentTypeValue}</span>}
      {constraints && (
        <span className="ml-1.5 text-muted-foreground text-xs">
          {[
            constraints.minLength != null && `min:${constraints.minLength}`,
            constraints.maxLength != null && `max:${constraints.maxLength}`,
            constraints.minimum != null && `≥${constraints.minimum}`,
            constraints.maximum != null && `≤${constraints.maximum}`,
          ]
            .filter(Boolean)
            .join(' ')}
        </span>
      )}
    </>
  );
}
